/**
 * Bait Panel
 * Right sidebar: bait/fly pattern quick-select tiles and scavenge worms button.
 * Adapts heading and scavenge visibility based on fly rod vs conventional rod.
 */

import { useState, useCallback } from 'react';
import { BaitSelector } from './BaitSelector';
import { useEquippedGear, useGameStore } from '@/state';
import { equipmentManager } from '@/systems';
import { isEquippedRodFlyRod } from '@/data/equipment/slotConfig';
import { BALANCE_CONFIG } from '@/data';
import { WormDigOverlay } from '@/components/wormDig';
import type { WormDigConfig, WormDigResult } from '@/types';

export function BaitPanel() {
  const equippedGear = useEquippedGear();
  const isFlyRod = isEquippedRodFlyRod(equippedGear);

  return (
    <div className="p-3 space-y-3">
      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">
        {isFlyRod ? 'Fly Patterns' : 'Bait & Scavenge'}
      </h3>

      <BaitSelector />
      {/* Only show scavenge button for conventional rods */}
      {!isFlyRod && <ScavengeButton />}
    </div>
  );
}

function ScavengeButton() {
  const [digConfig, setDigConfig] = useState<WormDigConfig | null>(null);
  const wormCount = useGameStore(
    (state) => state.playerState.ownedEquipment['bait_worms']?.quantity ?? 0,
  );

  const handleComplete = useCallback((result: WormDigResult) => {
    // Award bait for each worm found
    for (let i = 0; i < result.baitAwarded; i++) {
      equipmentManager.scavengeWorms();
    }
    setDigConfig(null);
  }, []);

  const handleScavenge = () => {
    if (digConfig) return; // Already digging

    const { WORM_DIG_MIN_WORMS, WORM_DIG_MAX_WORMS } = BALANCE_CONFIG;
    const wormDigCount = WORM_DIG_MIN_WORMS +
      Math.floor(Math.random() * (WORM_DIG_MAX_WORMS - WORM_DIG_MIN_WORMS + 1));

    setDigConfig({
      wormCount: wormDigCount,
      revealThreshold: BALANCE_CONFIG.WORM_DIG_REVEAL_THRESHOLD,
      digRadius: BALANCE_CONFIG.WORM_DIG_BRUSH_RADIUS,
      wormHitRadius: BALANCE_CONFIG.WORM_DIG_WORM_HIT_RADIUS,
      maxParticles: BALANCE_CONFIG.WORM_DIG_MAX_PARTICLES,
      timeoutMs: BALANCE_CONFIG.WORM_DIG_TIMEOUT_MS,
      wormsPerFind: BALANCE_CONFIG.WORM_DIG_BAIT_PER_WORM,
      onComplete: handleComplete,
    });
  };

  return (
    <>
      <button
        onClick={handleScavenge}
        className="w-full px-4 py-2 border rounded-lg text-sm transition-colors min-h-[48px] bg-amber-700/60 hover:bg-amber-700 border-amber-600/50 text-amber-200 cursor-pointer"
      >
        <span className="mr-1">🪱</span>
        Scavenge Worms
        <span className="ml-2 text-amber-400/80 text-xs">({wormCount})</span>
      </button>

      <WormDigOverlay config={digConfig} />
    </>
  );
}
